import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { LINE_STATUSES, ORDER_STATUSES } from '../firebase/schema';

/**
 * Scans all orders and updates any that have all lines completed
 * but the order status is not "Ready To Invoice"
 */
export const fixOrphanedInvoiceableOrders = async () => {
  try {
    console.log('🔍 Scanning for orders with all lines completed...');
    
    // Get all non-completed orders
    const ordersQuery = query(
      collection(db, 'orders'),
      where('status', '!=', ORDER_STATUSES.COMPLETED)
    );
    
    const ordersSnapshot = await getDocs(ordersQuery);
    const orders = ordersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`📦 Found ${orders.length} non-completed orders to check`);
    
    const updatedOrders = [];
    
    // Check each order
    for (const order of orders) {
      // Fetch all lines for this order
      const linesQuery = query(
        collection(db, 'orderLines'),
        where('orderId', '==', order.id)
      );
      
      const linesSnapshot = await getDocs(linesQuery);
      const lines = linesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Check if all lines are completed
      const allCompleted = lines.length > 0 && lines.every(line => line.status === LINE_STATUSES.COMPLETED);
      
      // If all lines are completed but order status is not Ready To Invoice
      if (allCompleted && order.status !== ORDER_STATUSES.READY_TO_INVOICE) {
        console.log(`✅ Found orphaned order: ${order.poNumber || order.id} - ${order.customerName}`);
        console.log(`   Current status: ${order.status} → Updating to: ${ORDER_STATUSES.READY_TO_INVOICE}`);
        
        // Update order status
        const orderRef = doc(db, 'orders', order.id);
        await updateDoc(orderRef, {
          status: ORDER_STATUSES.READY_TO_INVOICE,
          updatedAt: serverTimestamp()
        });
        
        updatedOrders.push({
          id: order.id,
          poNumber: order.poNumber,
          customerName: order.customerName,
          oldStatus: order.status
        });
      }
    }
    
    console.log(`✨ Fixed ${updatedOrders.length} orphaned orders`);
    
    return {
      success: true,
      updatedCount: updatedOrders.length,
      updatedOrders
    };
  } catch (error) {
    console.error('❌ Error fixing orphaned orders:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Check a single order and update if needed
 */
export const checkAndFixSingleOrder = async (orderId) => {
  try {
    // Fetch all lines for this order
    const linesQuery = query(
      collection(db, 'orderLines'),
      where('orderId', '==', orderId)
    );
    
    const linesSnapshot = await getDocs(linesQuery);
    const lines = linesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    // Check if all lines are completed
    const allCompleted = lines.length > 0 && lines.every(line => line.status === LINE_STATUSES.COMPLETED);
    
    if (allCompleted) {
      // Get current order status
      const orderRef = doc(db, 'orders', orderId);
      const orderSnap = await getDocs(orderRef);
      const currentStatus = orderSnap.data()?.status;
      
      if (currentStatus !== ORDER_STATUSES.READY_TO_INVOICE) {
        // Update order status
        await updateDoc(orderRef, {
          status: ORDER_STATUSES.READY_TO_INVOICE,
          updatedAt: serverTimestamp()
        });
        
        return {
          success: true,
          updated: true,
          oldStatus: currentStatus
        };
      }
    }
    
    return {
      success: true,
      updated: false,
      message: 'Order does not need updating'
    };
  } catch (error) {
    console.error('Error checking/fixing order:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

